import './Grafo.scss'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Graph } from 'react-d3-graph'
import { IPersonas, Personas } from '../../constants/personas'
import { Proyectos } from '../../constants/proyectos'
import { GrafoSearch } from './GrafoSearch/GrafoSearch'
import { Box, Grid, IconButton, Tooltip } from '@mui/material'
import PersonaSVG from '../../../assets/images/persona.svg'
import SocioSVG from '../../../assets/images/socio.svg'
import SocioSVGOn from '../../../assets/images/socios_on.svg'
import ProyectoSVG from '../../../assets/images/proyecto.svg'
import ProyectoSVGOn from '../../../assets/images/proyectos_on.svg'
import EmpresaSVG from '../../../assets/images/empresa.svg'
import EmpresaSVGOn from '../../../assets/images/empresas_on.svg'
import GrafoPlaceholder from '../../../assets/images/grafo_bg_placeholder.png'
import { Empresas, IEmpresas } from '../../constants/empresas'
import { GrafoInfoCard } from './GrafoInfoCard/GrafoInfoCard'
import { GrafoConfig, NodeData, SearchData } from './Grafo.constants'
// import UndoIcon from '@mui/icons-material/Undo'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
// import DeleteIcon from '@mui/icons-material/Delete'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import { useTranslation } from 'react-i18next'
import { IProyectos } from '../../data/data.model'

export const Grafo = () => {
  const { t }: any = useTranslation()
  const [isMobile, setIsMobile] = useState(
    window.matchMedia('(max-width: 968px)').matches
  )
  const [focusNodeID, setFocusNodeId] = useState('')
  const [defaultSearchTerm, setDefaultSearchTerm] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [searchedNode, setSearchedNode] = useState<SearchData>()
  const [history, setHistory] = useState<SearchData[]>([])
  const [searchCurrentIndex, setSearchCurrentIndex] = useState<number | null>(
    null
  ) // Índice actual en el historial

  const [isFocusFromClick, setIsFocusFromClick] = useState(false)

  const [isSearching, setIsSearching] = useState<boolean>(false)
  const [cardData, setCardData] = useState<IPersonas | IEmpresas | IProyectos>()
  const [grafoData, setGrafoData] = useState<{
    nodes: NodeData[]
    links: any[]
  }>({
    nodes: [],
    links: [],
  })

  const getSVGTypeIcon = (nodeType: string, on = false): any => {
    switch (nodeType) {
      case 'persona':
        return PersonaSVG
      case 'empresa':
        return !on ? EmpresaSVG : EmpresaSVGOn
      case 'proyecto':
        return !on ? ProyectoSVG : ProyectoSVGOn
      case 'socio':
        return !on ? SocioSVG : SocioSVGOn
    }
  }

  const searchData = useMemo(() => {
    const sociosNodos: SearchData[] = Personas.map((persona) => ({
      id: persona.id_socio_carbono,
      nodeType: 'socio',
      label: persona.nome_socio,
      info: persona,
    }))
    const proyectosNodos: SearchData[] = Proyectos.map((proyecto) => ({
      id: proyecto.id_proyecto_carbono,
      nodeType: 'proyecto',
      label: proyecto.nombre_proyecto,
      info: proyecto,
    }))
    const empresaNodos: SearchData[] = Empresas.map((empresa) => ({
      id: empresa.id_empresa_carbono,
      nodeType: 'empresa',
      label: empresa.nombre_empresa,
      info: empresa,
    }))

    return [...sociosNodos, ...proyectosNodos, ...empresaNodos]
  }, [])

  useEffect(() => {
    setGrafoData({ nodes: [], links: [] })
    if (searchedNode) {
      const nodes = []
      const sociosNodos: NodeData[] = []
      const empresasNodos: NodeData[] = []
      const proyectosNodos: NodeData[] = []

      const nodeType = searchedNode.nodeType

      switch (nodeType) {
        case 'socio':
          const empresasSocio = Empresas.filter((empresa: IEmpresas) =>
            searchedNode.info.relacao_empresa_rol
              .map((relacion: string) => relacion.split(':')[0])
              .includes(empresa.id_empresa_carbono)
          )

          const uniqueProyectosEmpresas = new Set()
          // busca los proyectos de las empresas relacionadas al socio
          // y los agrega a un set para evitar duplicados
          empresasSocio.forEach((empresa: IEmpresas) => {
            Proyectos.filter((proyecto: IProyectos) =>
              proyecto.id_empresas_carbono_desarrolladora.includes(
                empresa.id_empresa_carbono
              )
            )
              .map((proyecto: IProyectos) => proyecto.id_proyecto_carbono)
              .forEach((proyectoId: string) =>
                uniqueProyectosEmpresas.add(proyectoId)
              )
          })

          const proyectosEmpresa = Proyectos.filter((proyecto: IProyectos) =>
            Array.from(uniqueProyectosEmpresas).includes(
              proyecto.id_proyecto_carbono
            )
          )

          sociosNodos.push({
            ...searchedNode,
            svg: getSVGTypeIcon(nodeType, true),
          })

          empresasNodos.push(
            ...empresasSocio.map((empresa: IEmpresas) => ({
              id: empresa.id_empresa_carbono,
              svg: getSVGTypeIcon('empresa'),
              label: empresa.nombre_empresa,
              info: empresa,
              nodeType: 'empresa',
            }))
          )

          proyectosNodos.push(
            ...proyectosEmpresa.map((proyecto: IProyectos) => ({
              id: proyecto.id_proyecto_carbono,
              svg: getSVGTypeIcon('proyecto'),
              label: proyecto.nombre_proyecto,
              info: proyecto,
              nodeType: 'proyecto',
            }))
          )

          break
        case 'empresa':
          const empresaSocios = Personas.filter((persona: IPersonas) =>
            persona.relacao_empresa_rol
              .map((relacion: string) => relacion.split(':')[0])
              .includes(searchedNode.info.id_empresa_carbono)
          )

          const empresaProyectos = Proyectos.filter((proyecto: IProyectos) =>
            searchedNode.info.relacion_proyecto.includes(
              proyecto.id_proyecto_carbono
            )
          )

          sociosNodos.push(
            ...empresaSocios.map((persona: IPersonas) => ({
              id: persona.id_socio_carbono,
              svg: getSVGTypeIcon('socio'),
              label: persona.nome_socio,
              info: persona,
              nodeType: 'socio',
            }))
          )

          empresasNodos.push({
            ...searchedNode,
            svg: getSVGTypeIcon(nodeType, true),
          })

          proyectosNodos.push(
            ...empresaProyectos.map((proyecto: IProyectos) => ({
              id: proyecto.id_proyecto_carbono,
              svg: getSVGTypeIcon('proyecto'),
              label: proyecto.nombre_proyecto,
              info: proyecto,
              nodeType: 'proyecto',
            }))
          )

          break
        case 'proyecto':
          const proyectoEmpresas = Empresas.filter((empresa: IEmpresas) =>
            empresa.relacion_proyecto.includes(
              searchedNode.info.id_proyecto_carbono
            )
          )
          const uniqueSociosEmpresas = new Set()
          proyectoEmpresas.forEach((empresa: IEmpresas) => {
            Personas.filter((persona: IPersonas) =>
              persona.relacao_empresa_rol.includes(empresa.id_empresa_carbono)
            )
              .map((persona: IPersonas) => persona.id_socio)
              .forEach((personaId: string) =>
                uniqueSociosEmpresas.add(personaId)
              )
          })

          const sociosEmpresas = Personas.filter((persona: IPersonas) =>
            Array.from(uniqueSociosEmpresas).includes(persona.id_socio)
          )

          sociosNodos.push(
            ...sociosEmpresas.map((persona: IPersonas) => ({
              id: persona.id_socio,
              svg: getSVGTypeIcon('socio'),
              label: persona.nome_socio,
              info: persona,
              nodeType: 'socio',
            }))
          )

          proyectosNodos.push({
            ...searchedNode,
            svg: getSVGTypeIcon(nodeType, true),
          })

          empresasNodos.push(
            ...proyectoEmpresas.map((empresa: IEmpresas) => ({
              id: empresa.id_empresa_carbono,
              svg: getSVGTypeIcon('empresa'),
              label: empresa.nombre_empresa,
              info: empresa,
              nodeType: 'empresa',
            }))
          )

          break
      }

      // Usamos un Set para enlaces únicos y validamos que los nodos existan
      const linksSet = new Set<string>()

      // create link entre proyectos y empresas
      proyectosNodos.forEach((proyecto) => {
        empresasNodos
          .filter((empresa) =>
            proyecto.info.id_empresas_carbono_desarrolladora.includes(
              empresa.info.id_empresa_carbono
            )
          )
          .forEach((empresa) => {
            const linkKey = `${empresa.info.id_empresa_carbono}?${proyecto.info.id_proyecto_carbono}`
            linksSet.add(linkKey)
          })
      })

      //crea links entre personas y empresas
      sociosNodos.forEach((persona) => {
        const role =
          persona.info.relacao_empresa_rol.length > 0
            ? t(`relacion.${persona.info.relacao_empresa_rol[0].split(':')[1]}`)
            : ''
        empresasNodos
          .filter((empresa) => {
            return persona.info.relacao_empresa_rol
              .map((relacion: string) => relacion.split(':')[0])
              .includes(empresa.info.id_empresa_carbono)
          })
          .forEach((empresa) => {
            const linkKey = `${persona.info.id_socio_carbono}?${empresa.info.id_empresa_carbono}:${role}`
            linksSet.add(linkKey)
          })
      })

      // Convertimos el Set de enlaces en un array
      const links = Array.from(linksSet).map((link) => {
        const [actualLink, label] = link.split(':')
        const [source, target] = actualLink.split('?')
        return { source, target, label }
      })

      nodes.push(...sociosNodos, ...empresasNodos, ...proyectosNodos)

      setGrafoData({ nodes, links })
    }
  }, [searchedNode])

  useEffect(() => {
    if (grafoData && searchedNode?.id && !isFocusFromClick) {
      const nodesNumber = grafoData.nodes.length
      const timing =
        grafoData.nodes.length < 10
          ? grafoData.nodes.length * 200
          : grafoData.nodes.length * 100

      if (nodesNumber === 1) {
        setFocusNodeId(searchedNode?.id || '')

        return
      }

      setTimeout(() => {
        setFocusNodeId(searchedNode?.id || '')
      }, timing)
    }
  }, [grafoData])

  const containerRef = useRef(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const { offsetWidth, offsetHeight } = containerRef.current
        setDimensions({ width: offsetWidth, height: offsetHeight })
      }
    }

    updateSize()
    window.addEventListener('resize', updateSize)
    return () => window.removeEventListener('resize', updateSize)
  }, [])

  const config = {
    ...GrafoConfig,
    width: dimensions.width,
    height: dimensions.height,
  }

  const resetStates = (avoidHistory = false) => {
    setDefaultSearchTerm('')
    setFocusNodeId('')
    setCardData(undefined)
    setSearchedNode(undefined)
    setGrafoData({ nodes: [], links: [] })
    setIsModalOpen(false)
    if (avoidHistory) return
    setHistory([])
    setSearchCurrentIndex(null) // Reinicia el índice al restablecer el estado
  }

  const onClickNode = (nodeId: any) => {
    setFocusNodeId(nodeId)
    setIsFocusFromClick(true)

    setGrafoData((prev) => {
      const newNodes = prev.nodes.map((node) => {
        if (node.id === nodeId) {
          // cambia el icono al nodo seleccionado
          return {
            ...node,
            svg: getSVGTypeIcon(node.nodeType, true),
          }
        } else {
          return {
            ...node,
            svg: getSVGTypeIcon(node.nodeType),
          }
        }
      })

      return { ...prev, nodes: newNodes }
    })

    const data = grafoData.nodes.find((node: NodeData) => node.id === nodeId)

    setCardData(data?.info)
    // setSearchedNode(nodeId)
    setIsModalOpen(true)
  }

  const handleDoubleClickNode = (nodeId: any) => {
    if (searchedNode?.id === nodeId) {
      return
    }

    resetStates(true)

    setTimeout(() => {
      const data: SearchData = searchData.find(
        (node: SearchData) => node.id === nodeId
      ) || { id: '', nodeType: '', label: '', info: {} }

      setHistory((prevHistory) => {
        const updatedHistory = [...prevHistory, data]

        setSearchCurrentIndex(updatedHistory.length - 1) // Establece el índice al nuevo último elemento
        return updatedHistory
      })

      setCardData(data?.info)
      setDefaultSearchTerm(data?.label || '')
      setSearchedNode(data)
    }, 500)
  }

  const handleSearchClick = (target: any) => {
    setHistory((prevHistory) => {
      const updatedHistory = [...prevHistory, target]

      setSearchCurrentIndex(updatedHistory.length - 1) // Establece el índice al nuevo último elemento
      return updatedHistory
    })
    setIsSearching(true)

    setSearchedNode(target)
  }

  const handleSearchBack = () => {
    if (searchCurrentIndex !== null && searchCurrentIndex > 0) {
      const previousIndex = searchCurrentIndex - 1
      setSearchedNode(history[previousIndex])
      setSearchCurrentIndex(previousIndex) // Actualiza al índice anterior
      setDefaultSearchTerm(history[previousIndex].label)
    }
  }

  return (
    <Grid
      container
      sx={{
        marginBottom: '20px',
        border: '1px solid black',
        position: 'relative',
      }}
    >
      <Grid item xs={12} md={3} className="searchLegendContainer">
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px',
            }}
          >
            <Box className="grafo-search-icons">
              <Tooltip title={t('graph.previous_search')}>
                <IconButton
                  aria-label="previous_search"
                  onClick={handleSearchBack}
                  disabled={!searchedNode || searchCurrentIndex === 0}
                >
                  <KeyboardBackspaceIcon sx={{ fontSize: 28, color: '#fff' }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Box className="grafo-search-icons">
              <Tooltip title={t('graph.clear_search')}>
                <IconButton
                  aria-label="restore"
                  onClick={() => {
                    setDefaultSearchTerm('')
                    setIsSearching(false)
                    resetStates()
                  }}
                  disabled={!searchedNode}
                >
                  <DeleteForeverIcon sx={{ fontSize: 28, color: '#fff' }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Box>
            {/* <Box sx={{ position: 'absolute', top: 65, left: 23 }}> */}
            <GrafoSearch
              nodes={searchData}
              handleSearchClick={handleSearchClick}
              defaultSearchTerm={defaultSearchTerm}
              setDefaultSearchTerm={setDefaultSearchTerm}
              setIsFocusFromClick={setIsFocusFromClick}
            ></GrafoSearch>
          </Box>
        </Box>
        <Box className="grafo-leyenda-container">
          <ul className="grafo-leyenda">
            {/* <li>
            <img src={PersonaSVG} alt="Persona" />
            Persona
          </li> */}
            <li>
              <img src={ProyectoSVG} alt="Proyecto" />
              {t('graph.project')}
            </li>
            <li>
              <img src={EmpresaSVG} alt="Empresa" />
              {t('graph.company')}
            </li>
            <li>
              <img src={SocioSVG} alt="Socio" />
              {t('graph.partner')}
            </li>
          </ul>
        </Box>
      </Grid>
      <Grid
        item
        xs={12}
        md={9}
        style={{ padding: '0px', backgroundColor: 'rgba(0,0,0,0.20)' }}
      >
        <Box
          ref={containerRef}
          sx={{
            // border: '1px solid black',
            height: !isMobile ? '800px' : '500px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            position: 'relative',
          }}
        >
          {searchedNode || isSearching ? (
            <Graph
              id="graph-id"
              data={{ ...grafoData, focusedNodeId: focusNodeID }}
              config={config}
              onClickNode={onClickNode}
              onDoubleClickNode={handleDoubleClickNode}
            />
          ) : (
            <>
              <span
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: '#fff',
                  fontSize: '16px',
                  textAlign: 'center',
                  padding: '5px 10px',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  maxWidth: 180,
                }}
              >
                {t('graph.searchText')}
              </span>
              <img
                style={{ width: '80%', maxWidth: 400 }}
                src={GrafoPlaceholder}
                alt=""
              />
            </>
          )}
        </Box>
      </Grid>
      {isModalOpen ? (
        <GrafoInfoCard data={cardData} setIsModalOpen={setIsModalOpen} />
      ) : null}
    </Grid>
  )
}
